import { useCallback } from 'react';
import { useFormik } from 'formik';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import { countriesOptions } from '@/domain/countries';
import trainerApplyFormValidationSchema from './trainerApplyFormValidationSchema';

const initialApplyFormData = {
  firstname: '',
  lastname: '',
  email: '',
  country: '',
  phone: '',
};

export default function CoachesTrainingRequestForm() {
  const onApplyFormSubmit = useCallback((values, { setSubmitting }) => {
    setSubmitting(true);
    router.push({
      pathname: '/formacion-de-entrenadores-scrum/aplicar',
      query: values,
    });
  }, []);

  const router = useRouter();

  const form = useFormik({
    initialValues: initialApplyFormData,
    validationSchema: trainerApplyFormValidationSchema,
    validateOnMount: true,
    onSubmit: onApplyFormSubmit,
  });

  return (
    <form
      onSubmit={form.handleSubmit}
      className="bg-gray-800 text-white rounded-lg w-full p-6 lg:p-8 flex flex-col space-y-6"
    >
      <div className="flex-1 space-y-4 md:space-y-3">
        <div className="w-full flex items-center">
          <label className="w-full grid grid-cols-1 md:grid-cols-12 items-center gap-2" htmlFor="firstname">
            <div className="text-white font-semibold md:col-span-6">
              Nombres
            </div>
            <input
              className={clsx(
                'md:col-span-6 px-3 focus:outline-none bg-gray-700 border border-transparent rounded-md py-1 focus:border-transparent focus:ring-2 focus:ring-gray-500 focus:bg-gray-600 focus:ring-offset-2 focus:ring-offset-gray-800 font-medium placeholder-gray-500',
                form.isSubmitting === true && 'opacity-50',
              )}
              type="text"
              id="firstname"
              name="firstname"
              type="firstname"
              onChange={form.handleChange}
              value={form.values.firstname}
              disabled={form.isSubmitting}
              required
            />
          </label>
        </div>
        <div className="w-full flex items-center">
          <label className="items-center w-full grid grid-cols-1 md:grid-cols-12 gap-2" htmlFor="lastname">
            <div className="text-white font-semibold md:col-span-6">
              Apellidos
            </div>
            <input
              className={clsx(
                'md:col-span-6 px-3 focus:outline-none bg-gray-700 border border-transparent rounded-md py-1 focus:border-transparent focus:ring-2 focus:ring-gray-500 focus:bg-gray-600 focus:ring-offset-2 focus:ring-offset-gray-800 font-medium placeholder-gray-500',
                form.isSubmitting === true && 'opacity-50',
              )}
              type="text"
              id="lastname"
              name="lastname"
              type="lastname"
              onChange={form.handleChange}
              value={form.values.lastname}
              disabled={form.isSubmitting}
              required
            />
          </label>
        </div>
        <div className="w-full flex items-center">
          <label className="items-center w-full grid grid-cols-1 md:grid-cols-12 gap-2" htmlFor="email">
            <div className="text-white font-semibold md:col-span-6">
              Correo electrónico
            </div>
            <input
              className={clsx(
                'md:col-span-6 px-3 focus:outline-none bg-gray-700 border border-transparent rounded-md py-1 focus:border-transparent focus:ring-2 focus:ring-gray-500 focus:bg-gray-600 focus:ring-offset-2 focus:ring-offset-gray-800 font-medium placeholder-gray-500',
                form.isSubmitting === true && 'opacity-50',
              )}
              type="text"
              id="email"
              name="email"
              type="email"
              onChange={form.handleChange}
              value={form.values.email}
              disabled={form.isSubmitting}
              required
            />
          </label>
        </div>
        <div className="w-full flex items-center">
          <label className="items-center w-full grid grid-cols-1 md:grid-cols-12 gap-2" htmlFor="country">
            <div className="text-white font-semibold md:col-span-6">
              País
            </div>
            <select
              className={clsx(
                'md:col-span-6 px-3 focus:outline-none bg-gray-700 border border-transparent rounded-md py-1 focus:border-transparent focus:ring-2 focus:ring-gray-500 focus:bg-gray-600 focus:ring-offset-2 focus:ring-offset-gray-800 font-medium placeholder-gray-500',
                form.isSubmitting === true && 'opacity-50',
              )}
              type="text"
              placeholder="España"
              id="country"
              name="country"
              type="country"
              onChange={form.handleChange}
              value={form.values.country}
              disabled={form.isSubmitting}
              required
            >
              {countriesOptions().map(({ value, display }) => (
                <option key={value} value={value}>
                  {display}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="w-full flex items-center">
          <label className="items-center w-full grid grid-cols-1 md:grid-cols-12 gap-2" htmlFor="phone">
            <div className="text-white font-semibold md:col-span-6 flex-1 flex items-center">
              Teléfono
            </div>
            <input
              className={clsx(
                'md:col-span-6 px-3 focus:outline-none bg-gray-700 border border-transparent rounded-md py-1 focus:border-transparent focus:ring-2 focus:ring-gray-500 focus:bg-gray-600 focus:ring-offset-2 focus:ring-offset-gray-800 font-medium placeholder-gray-500',
                form.isSubmitting === true && 'opacity-50',
              )}
              type="text"
              id="phone"
              name="phone"
              type="phone"
              onChange={form.handleChange}
              value={form.values.phone}
              disabled={form.isSubmitting}
              required
            />
          </label>
        </div>
      </div>
      <div className="flex items-center md:justify-end">
        <button
          className={clsx(
            'rounded-full bg-green-600 inline-flex px-5 py-2 leading-5 font-semibold text-gray-900 transition ease-in-out duration-300',
            (form.isValid === false || form.isSubmitting === true) && 'opacity-50 pointer-events-none',
            (form.isValid === true && form.isSubmitting === false) && 'hover:bg-green-500 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 focus:ring-offset-gray-900',
          )} 
          disabled={form.isValid === false || form.isSubmitting === true}
          type="submit"
        >
          {form.isSubmitting ? 'Iniciando proceso' : 'Comenzar proceso'}
        </button>
      </div>
    </form>
  );
}
